/*
 * The app's pages. Signed-out pages (sign in, two-step, invites) sit beside the signed-in app; the app checks the session
 * once and sends people to sign in or two-step when needed. The preview build keeps the address in memory (it runs inside
 * a frame), the real app uses the browser's address bar.
 */
import { createMemoryHistory, createRouter } from "@tanstack/react-router";
import { authRoutes } from "./routes/auth";
import { publicRoutes } from "./routes/public";
import { crewRoutes } from "./routes/crew";
import { hrRoutes } from "./routes/hr";
import { officeRoutes } from "./routes/office";
import { clientRoutes } from "./routes/clients";
import { jobRoutes } from "./routes/jobs";
import { moneyRoutes } from "./routes/money";
import { salesRoutes } from "./routes/sales";
import { agencyRoutes } from "./routes/agency";
import { settingsTree } from "./routes/settings";
import { app, rootRoute } from "./routes/base";

// Every area lists its own routes (app/routes/); this only puts the tree together.
const routeTree = rootRoute.addChildren([
  ...authRoutes,
  ...publicRoutes,
  app.addChildren([
    ...crewRoutes,
    ...hrRoutes,
    ...officeRoutes,
    ...clientRoutes,
    ...jobRoutes,
    ...moneyRoutes,
    ...salesRoutes,
    ...agencyRoutes,
    settingsTree,
  ]),
]);

/** The preview opens on the office sign in, or on a page in its address when served (/pay/demo, /quote/demo, /my). */
const previewStart = () => {
  const { pathname, search } = window.location;
  return pathname === "/" || pathname.endsWith(".html") ? "/sign-in/office" : `${pathname}${search}`;
};

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  ...(__PREVIEW__ ? { history: createMemoryHistory({ initialEntries: [previewStart()] }) } : {}),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
